import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomerApi from '../../api/customer/CustomerApi';
import customerActions from '../../redux/customer/actions/CustomerActions.creators';
import { useDispatch, useSelector } from 'react-redux';
import { makeGetCustomerProfile } from '../../redux/customer/selectors/CustomerSelector';
import CustomerUtil from '../../util/CustomerUtil';
import { Box, Button, ButtonState, ButtonVariant, Column, Group, GroupAlign, GroupGutter, GroupVerticalAlign, Heading, HeadingType, Icon, IconName, IconSize, List, ListSpacing, Row, Text, TextAlign, TextColor, useIsMounted, } from '@as/cotopaxi';
import { AemConfigurationThirdpartySocialLoginProvider, } from '@as-react/common-types';
import { getLoginError } from '../../util/LoginUtil';
import AuthSocialLoginFacebookApi from '../../api/auth/AuthSocialLoginFacebookApi';
import { Divider } from '@yonderland/andes-react';
const accountIconMap = {
    facebook: IconName.FACEBOOK_BOX,
};
export const ConnectedAccounts = () => {
    const { t } = useTranslation(I18N_NAMESPACES.account);
    const { defaultRequestParams, servicesEndpoint, socialEnabledList } = useAemConfig();
    const [platforms, setPlatforms] = useState();
    const [removeLinkPaths, setRemoveLinkPaths] = useState([]);
    const isMounted = useIsMounted();
    const [buttonState, setButtonState] = useState(ButtonState.INTERACTIVE);
    const [authErrorMessage, setAuthErrorMessage] = useState();
    const dispatch = useDispatch();
    const profile = useSelector(makeGetCustomerProfile());
    const fetchAccounts = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const connectedAccountsResponse = yield CustomerApi.getSocialMediaConnections(servicesEndpoint, defaultRequestParams, profile.email, profile.customerIdentityId);
            if (isMounted()) {
                setPlatforms(CustomerUtil.sortSocialMediaConnections(connectedAccountsResponse, socialEnabledList));
                setButtonState(ButtonState.INTERACTIVE);
            }
        }
        catch (_a) { }
    }), [profile, isMounted]);
    useEffect(() => {
        if (!profile) {
            dispatch(customerActions.getProfile());
            return undefined;
        }
        fetchAccounts();
    }, [profile, fetchAccounts]);
    if (!platforms) {
        return null;
    }
    const handleRemoveConnectionLink = (connectionPath) => () => {
        setRemoveLinkPaths(prev => {
            const pathIndex = prev.indexOf(connectionPath);
            if (pathIndex < 0) {
                return [...prev, connectionPath];
            }
            prev.splice(pathIndex, 1);
            return prev;
        });
    };
    const handleRemoveConnection = (connection) => () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield CustomerApi.removeSocialMediaConnection(servicesEndpoint, defaultRequestParams, connection);
            fetchAccounts();
        }
        catch (_a) { }
    });
    const handleSocialLoginFacebook = () => __awaiter(void 0, void 0, void 0, function* () {
        setAuthErrorMessage(undefined);
        setButtonState(ButtonState.LOADING);
        try {
            yield AuthSocialLoginFacebookApi.addAccount();
            setButtonState(ButtonState.FINISHED);
            fetchAccounts();
        }
        catch (error) {
            setButtonState(ButtonState.INTERACTIVE);
            setAuthErrorMessage(error.message || error);
        }
    });
    return (_jsx(Row, { parent: true, children: _jsxs(Column, { tablet: 6, desktop: 6, children: [_jsx(Box, { mobile: { spacing: { marginBottom: 2 } }, children: _jsx(Heading, { type: HeadingType.SMALL_TITLE, children: t('profileDetails.accounts.heading') }) }), platforms.map((platform, index) => (_jsxs(Box, { mobile: { spacing: { marginTop: index === 0 ? 0 : 3 } }, children: [_jsx(Box, { mobile: { spacing: { marginBottom: 1 } }, children: _jsxs(Group, { column: true, gutterY: GroupGutter.XSMALL, children: [_jsx(Icon, { name: accountIconMap[platform.title], size: IconSize.EXTRA_MAJOR }), _jsx(Text, { emphasized: true, children: t('account.social.media.connections.linked.account', {
                                            platform: platform.title,
                                        }) })] }) }), _jsx(Divider, {}), platform.connections.length === 0 ? (_jsxs(_Fragment, { children: [_jsx(Box, { mobile: { spacing: { paddingTop: 1, paddingBottom: 1 } }, children: _jsx(Text, { color: TextColor.SUBTLE, align: TextAlign.CENTER, children: t('account.social.media.connections.none') }) }), _jsx(Divider, {})] })) : (_jsx(List, { spacing: ListSpacing.MEDIUM, children: platform.connections.map((connection, connectionIndex) => {
                                const connectionPath = `platforms.${index}.connections.${connectionIndex}`;
                                return (_jsxs(List.Item, { children: [_jsxs(Box, { mobile: { spacing: { paddingTop: 1, paddingBottom: 1 } }, children: [_jsxs(Group, { valign: GroupVerticalAlign.CENTER, gutterX: GroupGutter.SMALL, align: GroupAlign.SPACE_BETWEEN, children: [_jsxs("div", { children: [_jsx(Text, { emphasized: true, children: connection.name }), connection.email && _jsxs(Text, { color: TextColor.SUBTLE, children: ["(", connection.email, ")"] })] }), _jsx(Group, { valign: GroupVerticalAlign.BOTTOM, column: true, children: _jsx("div", { children: _jsx(Button, { onClick: handleRemoveConnectionLink(connectionPath), text: t('account.social.media.connections.disconnect.button') }) }) })] }), _jsx("div", { children: removeLinkPaths.includes(connectionPath) && (_jsx(Button, { onClick: handleRemoveConnection(connection), variant: ButtonVariant.LINK, text: t('account.social.media.connections.disconnect.link', {
                                                            email: connection.email,
                                                        }) })) })] }), _jsx(Divider, {})] }, connectionIndex));
                            }) })), platform.title === AemConfigurationThirdpartySocialLoginProvider.FACEBOOK &&
                            platform.connections.length === 0 && (_jsxs(Box, { mobile: { spacing: { marginTop: 2 } }, children: [_jsx(Button, { fill: true, onClick: handleSocialLoginFacebook, variant: ButtonVariant.FACEBOOK, state: buttonState, text: t('account.social.media.connections.add.account', {
                                        platform: 'Facebook',
                                    }), iconLeft: { name: IconName.FACEBOOK_BOX } }), authErrorMessage && (_jsx(Box, { mobile: { spacing: { marginTop: 1 } }, children: _jsx(Text, { color: TextColor.NEGATIVE, children: getLoginError(authErrorMessage, t) }) }))] }))] }, index)))] }) }));
};
